import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  Inbox, Mail, Send, Archive, Clock, UserCheck, RefreshCw, Loader2,
  AlertTriangle, ChevronLeft, CornerUpLeft, Hash, Lock,
} from 'lucide-react';

// DCS ATS — the shared inbox.
// Three mailboxes (care@ / hr@ / smita@), one folder rail, one thread view.
// The rail only lists the mailboxes THIS user is a member of, and the thread
// list is filtered server-side to those mailboxes — so Smita never sees care@,
// and a recruiter sees only hr@. Access is membership, not role (see
// routes/mailbox.py compute_access). Reading is a ~2-min poll of Gmail; this
// page just renders what the poller has already stored.

const FOLDERS = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'unassigned', label: 'Unassigned', icon: Mail },
  { id: 'mine', label: 'Mine', icon: UserCheck },
  { id: 'awaiting', label: 'Awaiting reply', icon: Clock },
  { id: 'sent', label: 'Sent', icon: Send },
  { id: 'archived', label: 'Archived', icon: Archive },
];

const fmtTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const ACCESS_BADGE = { manage: 'Manage', send: 'Send', read: 'Read' };

export default function MailPage() {
  const [mailboxes, setMailboxes] = useState([]);
  const [myTag, setMyTag] = useState(null);
  const [mailboxId, setMailboxId] = useState('');     // '' = all my mailboxes
  const [folder, setFolder] = useState('inbox');
  const [threads, setThreads] = useState([]);
  const [openThread, setOpenThread] = useState(null); // {thread, messages}
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');

  const loadMailboxes = useCallback(async () => {
    try {
      const r = await api.get('/mail/mailboxes');
      setMailboxes(r.data.mailboxes || []);
      setMyTag(r.data.reply_tag || null);
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
  }, []);

  const loadThreads = useCallback(async () => {
    try {
      const params = new URLSearchParams({ folder });
      if (mailboxId) params.set('mailbox_id', mailboxId);
      const r = await api.get(`/mail/threads?${params.toString()}`);
      setThreads(r.data.threads || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setLoading(false);
  }, [folder, mailboxId]);

  useEffect(() => { loadMailboxes(); }, [loadMailboxes]);
  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 120000); // mirrors the ~2-min poll cadence
    return () => clearInterval(t);
  }, [loadThreads]);

  const sync = async () => {
    setBusy('sync');
    try {
      await api.post('/mail/hooks/poll', mailboxId ? { mailbox_id: mailboxId } : {});
      await loadThreads();
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  const open = async (id) => {
    setBusy(`open-${id}`);
    try {
      const r = await api.get(`/mail/threads/${id}`);
      setOpenThread(r.data);
      setReply('');
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  const claim = async (id) => {
    setBusy('claim');
    try {
      await api.post(`/mail/threads/${id}/claim`);
      await open(id);
      await loadThreads();
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  const sendReply = async () => {
    if (!openThread || !reply.trim()) return;
    const th = openThread.thread;
    const last = openThread.messages[openThread.messages.length - 1] || {};
    const to = last.from_email && last.direction === 'inbound'
      ? last.from_email
      : (th.participants || [])[0];
    setBusy('send');
    try {
      await api.post('/mail/send', {
        mailbox_id: th.mailbox_id,
        to_email: to,
        subject: (th.subject || '').startsWith('Re:') ? th.subject : `Re: ${th.subject || ''}`,
        body_html: `<div style="font-family:Arial,sans-serif;font-size:14px;color:#0f172a;line-height:1.6">${
          reply.replace(/\n/g, '<br>')}</div>`,
        thread_id: th.id,
      });
      await open(th.id);
      await loadThreads();
      setReply('');
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="mail-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-700" /> Mailbox
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Shared inbox across the mailboxes you can access. Client replies route
            back to the recruiter automatically by reply tag.
            {myTag && (
              <span className="ml-1 inline-flex items-center gap-1 text-slate-600">
                <Hash className="w-3 h-3" />your tag: <code className="text-slate-700">hr+{myTag}@</code>
              </span>
            )}
          </p>
        </div>
        <button
          onClick={sync} disabled={!!busy}
          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
          data-testid="mail-sync"
        >
          {busy === 'sync' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Sync now
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 mb-3 text-xs text-red-600 bg-red-50 rounded-lg">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /><span className="break-all">{error}</span>
        </div>
      )}

      {/* Mailbox switcher — only the ones this user is a member of */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="mailbox-switcher">
        <button
          onClick={() => setMailboxId('')}
          className={`text-xs px-3 py-1.5 rounded-full border ${
            mailboxId === '' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
        >
          All my mail
        </button>
        {mailboxes.map((mb) => (
          <button
            key={mb.id}
            onClick={() => setMailboxId(mb.id)}
            title={`${mb.email} · ${ACCESS_BADGE[mb.access] || mb.access}`}
            className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 ${
              mailboxId === mb.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
          >
            {mb.id === 'care' && <Lock className="w-3 h-3" />}
            {mb.email}
            <span className="opacity-60">· {ACCESS_BADGE[mb.access] || mb.access}</span>
          </button>
        ))}
        {mailboxes.length === 0 && (
          <span className="text-xs text-slate-400">No mailbox access. Ask an admin to add you.</span>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Folder rail */}
        <div className="col-span-12 md:col-span-3">
          <nav className="bg-white border border-slate-200 rounded-xl p-2 space-y-0.5">
            {FOLDERS.map((f) => {
              const Icon = f.icon;
              return (
                <button
                  key={f.id}
                  onClick={() => { setFolder(f.id); setOpenThread(null); }}
                  className={`w-full flex items-center gap-2 text-sm px-3 py-2 rounded-lg text-left ${
                    folder === f.id ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`folder-${f.id}`}
                >
                  <Icon className="w-4 h-4" /> {f.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Thread list / thread view */}
        <div className="col-span-12 md:col-span-9">
          {!openThread ? (
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              {threads.length === 0 && (
                <div className="py-12 text-center text-slate-400 text-sm">Nothing here.</div>
              )}
              {threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => open(t.id)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3"
                  data-testid="thread-row"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`truncate ${t.unread ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                        {t.last_from_name || t.last_from_email || '(unknown)'}
                      </p>
                      {t.awaiting_reply && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Awaiting</span>
                      )}
                      {!t.owner_id && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Unclaimed</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-800 truncate">{t.subject}</p>
                    <p className="text-xs text-slate-400 truncate">{t.snippet}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-slate-400">{fmtTime(t.last_message_at)}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{t.mailbox_id}@</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <button onClick={() => setOpenThread(null)}
                        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <div className="flex items-center gap-2">
                  {!openThread.thread.owner_id && (
                    <button onClick={() => claim(openThread.thread.id)} disabled={!!busy}
                            className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40">
                      <UserCheck className="w-3 h-3 inline -mt-0.5 mr-1" />Claim
                    </button>
                  )}
                </div>
              </div>

              <div className="p-4">
                <h2 className="text-base font-semibold text-slate-900 mb-3">{openThread.thread.subject}</h2>
                <div className="space-y-3">
                  {openThread.messages.map((m) => (
                    <div key={m.id}
                         className={`rounded-lg p-3 text-sm ${
                           m.direction === 'outbound' ? 'bg-sky-50 border border-sky-100' : 'bg-slate-50 border border-slate-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-slate-700">
                          {m.from_name || m.from_email}
                          {m.direction === 'outbound' && m.reply_to && (
                            <span className="ml-2 text-slate-400">reply-to {m.reply_to}</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400">{fmtTime(m.sent_at)}</p>
                      </div>
                      <div className="text-slate-800 whitespace-pre-wrap">
                        {m.body_text || m.snippet || ''}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply composer */}
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={4}
                    placeholder="Write a reply…"
                    className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    data-testid="reply-box"
                  />
                  <div className="flex items-center justify-end mt-2">
                    <button onClick={sendReply} disabled={busy === 'send' || !reply.trim()}
                            className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
                            data-testid="reply-send">
                      {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CornerUpLeft className="w-4 h-4" />}
                      Send reply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
